import React from 'react';
import { ClockIcon, EyeOffIcon, HourglassIcon, RepeatIcon, RotateCcwIcon, RouteIcon, TriangleAlertIcon, UsersIcon, XIcon } from 'lucide-react';
import { SectionHeading } from './SectionHeading';
import { arrivalLines, CostIcon, costs, finalCost, sessionLines } from '../data/copy';
const costIcons: Record<CostIcon, typeof ClockIcon> = {
  clock: ClockIcon,
  repeat: RepeatIcon,
  hourglass: HourglassIcon,
  route: RouteIcon,
  alert: TriangleAlertIcon,
  users: UsersIcon,
  eye: EyeOffIcon
};
export function ProblemSection() {
  return <section className="defer-paint w-full bg-navy py-10 sm:py-14" aria-labelledby="problem-heading" data-id="element-183" data-mp="35frbw1z2rssq">
      <div className="mx-auto max-w-6xl px-6" data-id="element-184" data-mp="3jfafqikushz8">
        <SectionHeading eyebrow="The real problem" eyebrowTone="danger" title={<span id="problem-heading" data-id="element-186" data-mp="3ef5yurg19l8h">Does this sound familiar?</span>} data-id="element-185" data-mp="3egh1up1nmoyj" />

        <div className="mx-auto mt-8 grid max-w-5xl gap-10 lg:grid-cols-2 lg:gap-16" data-id="element-187" data-mp="1p2kk9weu70pq">
          <div data-id="element-188" data-mp="1k49b7nwd8u8k">
            <p className="font-display text-base font-semibold uppercase tracking-[0.2em] text-muted" data-id="element-189" data-mp="1k4simaoe408c">
              Practice starts at
            </p>
            <p className="font-display text-7xl font-extrabold leading-none text-accent sm:text-8xl" data-id="element-190" data-mp="oboq4u2nr7j1">
              6:00 PM.
            </p>
            <ul className="mt-6 space-y-1.5 text-lg text-fg/90" data-id="element-191" data-mp="sc8n9xn8eb56">
              {arrivalLines.map(line => <li key={line} data-id="element-192" data-mp="x5behz4jd8i7">{line}</li>)}
            </ul>
            <p className="mt-6 font-display text-2xl font-bold uppercase leading-tight text-fg sm:text-3xl" data-id="element-193" data-mp="123v1m2uv8qc5">
              And you are still trying to decide what to coach.
            </p>
            <p className="mt-5 leading-relaxed text-muted" data-id="element-194" data-mp="1c1soon0zoh36">
              You spent part of the day searching YouTube, saving drills, taking
              screenshots, opening different websites, and trying to connect
              exercises from different sources.
            </p>
          </div>

          <div className="lg:border-l lg:border-line lg:pl-16" data-id="element-195" data-mp="3hur5au3r8x7u">
            <p className="font-display text-3xl font-bold uppercase leading-tight text-fg sm:text-4xl" data-id="element-196" data-mp="3hv0w2jwmj8el">
              Then practice begins.
            </p>
            <ul className="mt-6 divide-y divide-line border-y border-line" data-id="element-197" data-mp="k1z4xe5bg3rv">
              {sessionLines.map(line => <li key={line} className="flex items-start gap-3 py-3.5 text-[1.02rem] text-fg/90" data-id="element-198" data-mp="y175obh4njy6">
                  <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-danger/15" aria-hidden="true" data-id="element-199" data-mp="tz3rhf32q197">
                    <XIcon className="h-3 w-3 text-danger" strokeWidth={3} data-id="element-200" data-mp="17xaajp3d5wot" />
                  </span>
                  {line}
                </li>)}
            </ul>
            <p className="mt-6 text-muted" data-id="element-201" data-mp="1hnwh6tt3rcvd">And after spending hours preparing...</p>
            <p className="mt-1 font-display text-3xl font-extrabold uppercase leading-tight text-fg sm:text-4xl" data-id="element-202" data-mp="1cpbhqwlocx7z">
              the practice still feels <span className="text-danger" data-id="element-203" data-mp="1qq5olat2qq4j">improvised.</span>
            </p>
          </div>
        </div>

        <div className="mt-12 sm:mt-16" data-id="element-204" data-mp="1lscmotyt0ia2">
          <h3 className="mx-auto max-w-3xl text-center font-display text-3xl font-extrabold uppercase leading-tight text-fg sm:text-4xl" data-id="element-205" data-mp="25ckz9se0p26t">
            The real cost of not having the right system
          </h3>

          <ul className="mx-auto mt-8 grid max-w-4xl grid-cols-2 gap-2.5 sm:mt-10 sm:gap-4" data-id="element-206" data-mp="1wc6csppbhh1q">
            {costs.map((cost, index) => {
            const Icon = costIcons[cost.icon];
            const isLastOdd = index === costs.length - 1 && costs.length % 2 === 1;
            return <li key={cost.title} className={`flex flex-col gap-2.5 rounded-2xl border p-3.5 sm:flex-row sm:items-center sm:gap-4 sm:rounded-3xl sm:p-5 ${isLastOdd ? 'col-span-2 sm:mx-auto sm:w-[calc(50%-0.5rem)]' : ''}`} style={{
              backgroundColor: `${cost.color}0d`,
              borderColor: `${cost.color}33`
            }} data-id="element-207" data-mp="1rcjva42ghen4" data-mpi={index}>
                  <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl sm:h-12 sm:w-12 sm:rounded-2xl" style={{
                backgroundColor: cost.color
              }} aria-hidden="true" data-id="element-208" data-mp="13oafpjvu5n4f">
                    <Icon className="h-4 w-4 text-white sm:h-6 sm:w-6" strokeWidth={2} data-id="element-209" data-mp="10kl3jqrxg82a" />
                  </span>
                  <div className="min-w-0" data-id="element-210" data-mp="vocsaoguoz0e">
                    <p className="font-display text-base font-bold uppercase leading-[1.05] text-fg sm:text-xl" data-id="element-211" data-mp="qurscqvp5uyk">
                      {cost.title}
                    </p>
                    <p className="mt-1 text-[0.8rem] leading-snug text-muted sm:text-[0.9rem]" data-id="element-212" data-mp="huv954s2toaw">
                      {cost.detail.join(' ')}
                    </p>
                  </div>
                </li>;
          })}
          </ul>

          <div className="mx-auto mt-5 flex max-w-4xl flex-col items-center gap-3 rounded-3xl border-2 border-accent/40 bg-accent/[0.06] px-6 py-9 text-center sm:px-10 sm:py-12" data-id="element-213" data-mp="7vxgbpnmb8m3">
            <span className="flex h-14 w-14 items-center justify-center rounded-full bg-accent/15" aria-hidden="true" data-id="element-214" data-mp="22b8cgxi74zrr">
              <RotateCcwIcon className="h-7 w-7 text-accent" strokeWidth={2} data-id="element-215" data-mp="1ya6bab4pydn5" />
            </span>
            <p className="mt-2 font-display text-2xl font-extrabold uppercase leading-tight text-fg sm:text-3xl" data-id="element-216" data-mp="3g7jcemqn0kdo">
              {finalCost.title}
            </p>
            <p className="text-muted" data-id="element-217" data-mp="3pwjfoic6a1xo">{finalCost.lead}</p>
            <p className="mt-1 font-display text-4xl font-extrabold leading-[1.05] text-accent sm:text-5xl" data-id="element-218" data-mp="3px2zy5pap2ev">
              {finalCost.question}
            </p>
          </div>
        </div>
      </div>
    </section>;
}